// typeAccess.js — none/read/write ladder for role → type record access

export const TYPE_ACCESS_LEVELS = Object.freeze(['none', 'read', 'write']);

export const TYPE_ACCESS_RANK = Object.freeze({
  none: 0,
  read: 1,
  write: 2,
});

export const DEFAULT_TYPE_ACCESS = Object.freeze({
  default: 'write',
  types: Object.freeze({}),
});

export function normalizeTypeAccessLevel(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'read' || normalized === 'write' || normalized === 'none') {
    return normalized;
  }
  return null;
}

export function typeAccessAllows(level, requiredLevel = 'read') {
  const have = TYPE_ACCESS_RANK[normalizeTypeAccessLevel(level) || 'none'] ?? 0;
  const need = TYPE_ACCESS_RANK[normalizeTypeAccessLevel(requiredLevel) || 'read'] ?? 1;
  return have >= need;
}

export function normalizeTypeAccess(input) {
  const source = input && typeof input === 'object' && !Array.isArray(input)
    ? input
    : {};
  const defaultLevel = normalizeTypeAccessLevel(source.default) || DEFAULT_TYPE_ACCESS.default;
  const typesIn = source.types && typeof source.types === 'object' && !Array.isArray(source.types)
    ? source.types
    : {};
  const types = {};
  for (const [typeId, level] of Object.entries(typesIn)) {
    const id = String(typeId || '').trim();
    if (!id) continue;
    const normalized = normalizeTypeAccessLevel(level);
    if (!normalized) continue;
    types[id] = normalized;
  }
  return {
    default: defaultLevel,
    types,
  };
}

export function defaultTypeAccess() {
  return {
    default: DEFAULT_TYPE_ACCESS.default,
    types: {},
  };
}

export function isSystemTypeData(typeData) {
  if (!typeData || typeof typeData !== 'object') return false;
  return typeData.system === true || typeData.isSystem === true;
}

export function resolveEffectiveTypeAccessLevel({
  tier,
  roleKey,
  isCustom = false,
  roleDefinition = null,
  roleMissing = false,
  memberTypeAccess = null,
  typeId,
  isSystemType = false,
} = {}) {
  const normalizedTier = String(tier || '').trim().toLowerCase();
  if (normalizedTier === 'owner' || normalizedTier === 'admin') {
    return 'write';
  }

  if (isSystemType) {
    return 'write';
  }

  const cleanTypeId = String(typeId || '').trim();
  if (!cleanTypeId) {
    return 'none';
  }

  let access = defaultTypeAccess();

  if (isCustom) {
    if (roleMissing || !roleDefinition) {
      access = normalizeTypeAccess(memberTypeAccess);
    } else {
      access = normalizeTypeAccess(roleDefinition.typeAccess);
    }
  } else if (String(roleKey || '').trim().toLowerCase() === 'member') {
    access = normalizeTypeAccess(memberTypeAccess);
  } else {
    return 'none';
  }

  if (Object.prototype.hasOwnProperty.call(access.types, cleanTypeId)) {
    return access.types[cleanTypeId];
  }
  return access.default;
}
